import React, { useCallback, useEffect, useRef } from 'react';
import { Cross2Icon, CrossCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Button, TextField } from '@radix-ui/themes';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { confirmationModal } from 'helpers/confirmation';
import { useDebounce } from 'helpers/useDebounceHook';
import { useWindowSize } from 'helpers/useWindowSize';
import {
  clearInlineSearchResults,
  inlineSearchPlayers,
  setInlineSearchQuery,
} from 'store/inlineSearch/inlineSearchSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import type { PlayerInlineSearchResultType } from 'types/player';
import PlayerObjectSkeleton from './PlayerObjectSkeleton';
import PlayerUserObject from './PlayerUserObject';

const COOKIE_KEY = 'recent_results';

const SearchBar: React.FC = () => {
  const lastQuery = useRef<string>('');
  const navigate = useNavigate();

  const size = useWindowSize();
  const isMobileOnly = size.width < 768;
  const [open, setOpen] = React.useState(false);
  const ref = useRef<HTMLDivElement>(null);
  let { query } = useAppSelector((state) => state.inlineSearch);
  const { loading, players } = useAppSelector((state) => state.inlineSearch);
  const dispatch = useAppDispatch();

  const debouncedSearchTerm = useDebounce(query, 1000);
  useEffect(() => {
    if (query !== lastQuery.current && debouncedSearchTerm) {
      dispatch(inlineSearchPlayers(query));
      lastQuery.current = query;
    }
    // eslint-disable-next-line
  }, [debouncedSearchTerm]);

  const removeText = useCallback(() => {
    dispatch(setInlineSearchQuery(''));
    dispatch(clearInlineSearchResults());
  }, [dispatch]);

  useEffect(() => {
    if (query === '') {
      removeText();
    }
  }, [query, removeText]);

  if (!query) query = '';

  const handleClickOutside = (event: MouseEvent) => {
    if ((ref.current && !ref.current.contains(event.target as HTMLDivElement)) || event.target === ref.current) {
      setOpen(false);
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };

  const areThereResults = () => !loading && players?.length > 0;

  const areThereRecentResults = () =>
    localStorage.getItem(COOKIE_KEY) && JSON.parse(localStorage.getItem(COOKIE_KEY) ?? '');

  const onPlayerItemClick = (player: PlayerInlineSearchResultType) => {
    let items: PlayerInlineSearchResultType[] =
      localStorage.getItem(COOKIE_KEY) && JSON.parse(localStorage.getItem(COOKIE_KEY) ?? '');
    if (items) {
      if (!items.find((it) => it.id === player.id)) {
        items.unshift(player);
        items = items.slice(0, 5);
      }
    } else {
      items = [player];
    }
    localStorage.setItem(COOKIE_KEY, JSON.stringify(items));
    setOpen(false);
  };

  const clearRecentSearchResults = () => {
    localStorage.removeItem(COOKIE_KEY);
    dispatch(inlineSearchPlayers(query));
  };

  const onClickClearRecentResults = () => {
    confirmationModal('Sei sicuro di voler eliminare le ricerche recenti?', () => {
      clearRecentSearchResults();
    });
  };

  const playerList = () => {
    if (loading) {
      return Array(3)
        .fill(0)
        .map((_, i) => <PlayerObjectSkeleton key={i} />);
    } else if (areThereResults()) {
      return players?.map((player, i) => {
        if (isMobileOnly && i > 2) return;
        return (
          <div key={`${player.id}_${i}`} className='max-w-full min-w-0 overflow-hidden'>
            <PlayerUserObject
              {...player}
              onClickFn={onPlayerItemClick}
              linkToPlayer={true}
              type={'fit_player_profile'}
            />
          </div>
        );
      });
    } else if (areThereRecentResults()) {
      return (
        <>
          <div className='text-neutral-neutral-8 flex justify-between'>
            Ricerche Recenti <Cross2Icon onClick={onClickClearRecentResults} className='cursor-pointer' />
          </div>
          <div className='max-w-full min-w-0 overflow-hidden'>
            {JSON.parse(localStorage.getItem(COOKIE_KEY) ?? '')?.map(
              (player: PlayerInlineSearchResultType, i: number) => {
                if ((isMobileOnly && i > 2) || Object.keys(player).length < 3) return;
                return (
                  <div key={`${player.id}_${i}`} className='max-w-full min-w-0 overflow-hidden'>
                    <PlayerUserObject
                      {...player}
                      linkToPlayer={true}
                      type={'fit_player_profile'}
                      onClickFn={() => setOpen(false)}
                    />
                  </div>
                );
              },
            )}
          </div>
        </>
      );
    }
  };

  const searchButtonsClassName = 'flex-1 px-1 md:px-3 hover:cursor-pointer';
  const openContent = (
    <div className='mt-2 flex max-w-full min-w-0 flex-col gap-2 overflow-hidden text-sm'>
      <div className='max-w-full min-w-0 overflow-hidden'>{playerList()}</div>
      <div className='text-neutral-neutral-8'>Cerca per:</div>
      <div className='flex flex-wrap items-center justify-center gap-2 self-stretch'>
        <Button
          size={'2'}
          variant='solid'
          className={searchButtonsClassName}
          onClick={() => navigate(`/cerca?page=players&q=${query}`)}
        >
          <MagnifyingGlassIcon width='14' height='14' />
          Giocatori
        </Button>
        <Button
          size={'2'}
          variant='solid'
          className={searchButtonsClassName}
          onClick={() => navigate(`/cerca?page=competitions&q=${query}`)}
        >
          <MagnifyingGlassIcon width='14' height='14' />
          Tornei
        </Button>
        <Button
          size={'2'}
          variant='solid'
          className={searchButtonsClassName}
          onClick={() => navigate(`/cerca?page=clubs&q=${query}`)}
        >
          <MagnifyingGlassIcon width='14' height='14' />
          Circoli
        </Button>
      </div>
      <div className='flex flex-wrap items-center justify-center gap-2 self-stretch'>
        <Button
          size={'2'}
          variant='solid'
          className='bg-neutral-neutral-alpha-3 flex-1 hover:cursor-pointer'
          onClick={() => navigate(`/cerca?page=other_profiles&q=${query}`)}
        >
          Ricerca avanzata
        </Button>
      </div>
    </div>
  );

  const textFieldClassName = classNames(
    'w-20',
    'md:w-60',
    'bg-accent-accent-6',
    'border',
    'border-solid',
    'border-neutral-neutral-alpha-8',
    'rounded-md',
    'h-10',
    'hover:border-accent-accent-8',
    'hover:bg-accent-accent-3',
    'transition-all',
    'duration-300',
    'ease-out',
    'focus-within:bg-white',
    { 'w-72': open },
    { 'md:w-full': open },
  );

  const containerClassName = classNames(
    'inline-block',
    { 'max-w-full': isMobileOnly },
    { 'bg-neutral-neutral-1': open },
    { border: open },
    { 'border-solid': open },
    { 'border-neutral-neutral-5': open },
    { 'p-3': open },
    { 'rounded-md': open },
    { 'md:w-96': open },
    { absolute: open && !isMobileOnly },
    { fixed: open && isMobileOnly },
    { 'top-[-25px]': open && !isMobileOnly },
    { 'top-[0]': open && isMobileOnly && !Capacitor.isNativePlatform() },
    { 'top-16': open && isMobileOnly && Capacitor.isNativePlatform() },
    { 'right-[-80px]': open && !isMobileOnly },
    { 'left-1/2': open && isMobileOnly },
    { 'translate-x-[-50%]': open && isMobileOnly },
    { 'z-50': open },
  );

  return (
    <div className='relative'>
      <div
        ref={ref}
        className={containerClassName}
        onFocusCapture={() => {
          setOpen(true);
          document.addEventListener('mousedown', handleClickOutside);
        }}
      >
        <TextField.Root
          data-testid='search-bar'
          value={query}
          onChange={(event) => dispatch(setInlineSearchQuery(event.target.value))}
          placeholder='Cerca  giocatori, tornei, circoli…'
          className={textFieldClassName}
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='off'
          spellCheck='false'
          type='search'
        >
          <TextField.Slot>
            <MagnifyingGlassIcon height='18' width='18' />
          </TextField.Slot>
          {query && (
            <TextField.Slot>
              <CrossCircledIcon height='12' width='12' className='hover:cursor-pointer' onClick={removeText} />
            </TextField.Slot>
          )}
        </TextField.Root>
        {open && openContent}
      </div>
    </div>
  );
};

export default SearchBar;
